import { createToolboxXml } from './toolbox-xml';

/**
 * WHAT A CONDITIONAL FUNCTION MAY CONTAIN — one definition, read by two consumers.
 *
 * The editor strips these from the full toolbox to build the conditional palette
 * (`buildConditionalToolboxXml`); the generation gate refuses a body that emits anything outside the
 * remainder. Those are the same fact, and two copies of a fact drift — which is precisely the failure
 * `hasEmptyBody` was written to end, in this same pipeline.
 *
 * ⚠ THE EXCLUSIONS ARE NOT STYLE. A conditional body is compiled as a SYNCHRONOUS function and
 * `getConditionalFn` (ui-shared-service.service.ts) refuses one containing `await` outright — it caches a
 * function that answers `false` for ever. So every async block here is excluded because including it does
 * not produce a slow conditional, it produces an invisible section with no error anywhere.
 */
export const CONDITIONAL_EXCLUDED_BLOCK_TYPES: string[] = [
  'show_loading_screen',
  'hide_loading_screen',
  'show_toast',
  'hide_toast',
  'get_current_user',
  'get_users_list',
  'get_roles_list',
  'create_user',
  'edit_user',
  'logout_user',
  'set_navigation',
  'open_external_string',
  'get_query',
  'update_entity',
  'create_entity',
  'async_function_wrapper',
  'resolve_promise_by_group',
  // The landing page's auth pair. These live ONLY in the sandboxed toolbox, so the full-toolbox
  // computation below never saw them and neither list named them — which left both restricted lanes
  // offering them on exactly the page that has nothing else to offer. Both emit
  // `await this.authService...`, and `await` is the one thing getConditionalFn / getValueFn refuse
  // outright: a permanent `false`, or a silent fall back to the input's static value. Listed by TYPE
  // as well as by category so this stays true if they are ever added to the full toolbox.
  'auth_login',
  'auth_signup_found',
  'auth_send_password_reset',
  'push_new_element_to_array',
  'procedures_callreturn',
  'procedures_callnoreturn',
  'push_to_entity_list',
  'delete_from_entity_list',
  'push_to_function_entity',
  // People blocks (Plan 3): each awaits the confirm overlay.
  'person_remove',
  'person_reinstate',
  'person_erase',
  'person_transfer',
  'delete_record',
  'user_delete_account',
  'meta_page_subscription',
];

export const CONDITIONAL_EXCLUDED_CATEGORIES: string[] = [
  'Query',
  'API',
  'Bastion Flows',
  'Workflows',
  'Conditions',
  'Special Functions',
  // Sandboxed-palette only — the full toolbox has no Auth category, so this is a no-op for
  // `restrictedAllowedBlockTypes` and strips the whole section (not just its blocks, which would
  // leave an empty category in the flyout) from the landing page's conditional / value palettes.
  'Auth',
];

/** Injected by the editor after the strips — the only way a conditional function answers. */
export const CONDITIONAL_EXTRA_BLOCK_TYPES: string[] = ['return_boolean'];

/**
 * Every block type a conditional function may contain, computed from the REAL toolbox rather than listed
 * by hand: the full toolbox, minus the excluded categories, minus the excluded types, plus
 * `return_boolean`.
 *
 * `procedures_defnoreturn` is added because every stored function is wrapped in one — all 36 conditional
 * functions in the corpus carry it — even though it is not offered in the palette.
 *
 * COMMENTS ARE STRIPPED BEFORE READING TYPES. The toolbox keeps deprecated blocks commented out rather
 * than deleted (`entity_conditions`, `aaob_filter_by_label`, and eleven more), so reading `type="..."`
 * off the raw string would report a block hidden a year ago as available today.
 */
export function conditionalAllowedBlockTypes(): Set<string> {
  return restrictedAllowedBlockTypes(CONDITIONAL_EXTRA_BLOCK_TYPES);
}

/** Injected by the editor after the strips — the only way a VALUE function answers. */
export const VALUE_EXTRA_BLOCK_TYPES: string[] = ['return_value'];

/**
 * Shared body for both restricted lanes: the full toolbox, minus the excluded categories, minus the
 * excluded types, plus whatever return block the lane injects, plus `procedures_defnoreturn`.
 *
 * ONE implementation on purpose. The editor's palette and this allowed-set are the same fact, and
 * the whole reason `conditionalAllowedBlockTypes` was written this way was to stop two copies of
 * that fact drifting. Adding a second lane by copy-paste would have reintroduced exactly that.
 */
function restrictedAllowedBlockTypes(extra: string[]): Set<string> {
  let xml = createToolboxXml();
  for (const cat of CONDITIONAL_EXCLUDED_CATEGORIES) {
    xml = xml.replace(new RegExp(`<category\\s+name="${cat}"[\\s\\S]*?<\\/category>`, 'g'), '');
  }
  xml = xml.replace(/<!--[\s\S]*?-->/g, '');
  const allowed = new Set<string>();
  for (const m of xml.matchAll(/<block[^>]*type="([^"]+)"/g)) allowed.add(m[1]);
  for (const t of CONDITIONAL_EXCLUDED_BLOCK_TYPES) allowed.delete(t);
  for (const t of extra) allowed.add(t);
  allowed.add('procedures_defnoreturn');
  return allowed;
}

/**
 * Every block type a VALUE function may contain — the conditional set with `return_value` in place
 * of `return_boolean`.
 *
 * DELIBERATELY the same exclusion lists. One list, two lanes, so they cannot drift; the known cost
 * is that the value lane inherits the conditional palette's six page-state setters (whose writes
 * land in the discarded sandbox clone) and text_print / text_prompt_ext. Fixing the shared lists
 * later fixes both lanes at once — which is the point.
 */
export function valueAllowedBlockTypes(): Set<string> {
  const allowed = restrictedAllowedBlockTypes(VALUE_EXTRA_BLOCK_TYPES);
  allowed.delete('return_boolean');
  return allowed;
}
